import React, { useMemo, useState } from 'react';
import ReactCrop from 'react-image-crop';

import dataURIToBlob from '../utils/dataURIToBlob';
import getAdjustedCrop from '../utils/getAdjustedCrop';
import getCropBox from '../utils/getCropBox';
import getPreview from '../utils/getPreview';
import getRotatedVersions from '../utils/getRotatedVersions';
import styles from './imageCropper.scss';

interface Initial {
  rotateImage: () => void;
  rotateImageRight: () => void;
  rotateImageLeft: () => void;
  saveImage: () => void;
  updateAspectRatio: (arg0: number) => void;
}

export const ImageCropperContext = React.createContext<Initial>({} as Initial);

export interface Crop {
  x: number;
  y: number;
  width: number;
  height: number;
  aspect: number;
}

export interface Save {
  file: Blob;
  crop: Crop;
  imagePreview: string;
  rotation: number;
}

interface Props {
  file: HTMLImageElement;
  onSave: (info: Save) => void;
  children: (arg0: React.ReactElement) => React.ReactElement;
  aspectRatio?: number;
  className?: string;
}

export const FlexImageCropper: React.FC<Props> = ({ file, onSave, children, aspectRatio, className }) => {
  const imageRotations: HTMLImageElement[] = useMemo(() => getRotatedVersions(file), [file]);
  const [rotatedIndex, setRotatedIndex] = useState(0);
  const [crop, setCrop] = useState(() => getCropBox(imageRotations, rotatedIndex, aspectRatio));

  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });

  const rotateImage = (): void => setRotatedIndex(rotatedIndex >= 3 ? 0 : rotatedIndex + 1);
  const rotateImageRight = (): void => setRotatedIndex(rotatedIndex >= 3 ? 0 : rotatedIndex + 1);
  const rotateImageLeft = (): void => setRotatedIndex(rotatedIndex <= 0 ? 3 : rotatedIndex - 1);
  const updateAspectRatio = (ratio: any): void => setCrop(getCropBox(imageRotations, rotatedIndex, ratio));

  const saveImage = (): void => {
    const adjustedCrop = getAdjustedCrop(crop, imageRotations[rotatedIndex], imageDimensions);
    getPreview({
      base64src: imageRotations[rotatedIndex].src,
      adjustedCrop,
      callback: imagePreview => {
        let rotation = Math.abs((4 - rotatedIndex) * 90);

        if (rotation === 360) {
          rotation = 0;
        }

        onSave({
          file: dataURIToBlob(imageRotations[rotatedIndex].src),
          crop: adjustedCrop,
          imagePreview,
          rotation,
        });
      },
    });
  };

  const cropper = (
    <div className={`${styles.reactCrop} ${className || ''}`}>
      {crop && (
        <ReactCrop
          src={imageRotations[rotatedIndex].src}
          keepSelection
          crop={crop}
          onChange={(cropData: any): void => {
            if (cropData.width && cropData.height) {
              setCrop(cropData);
            }
          }}
          onImageLoaded={(image: any): void => {
            setImageDimensions({
              width: image.width,
              height: image.height,
            });
          }}
        />
      )}
    </div>
  );

  const context = {
    rotateImage,
    saveImage,
    rotateImageLeft,
    rotateImageRight,
    updateAspectRatio,
  };

  return (
    <>
      <ImageCropperContext.Provider value={context}>{children && children(cropper)}</ImageCropperContext.Provider>
    </>
  );
};
